import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button, Card, dayValue, hourValue, momentOf } from '@wundo/ui';
import {
  FirstMeetingFields,
  type FirstMeetingDraft,
} from '@/components/onboarding/FirstMeetingFields.tsx';
import {
  PlannedMeetingsPreview,
  type PlannedMeeting,
} from '@/components/onboarding/PlannedMeetingsPreview.tsx';
import { defaultStart } from '@/components/meetings/manualMeetingTime.ts';
import { defaultMeetingTemplateId } from '@/components/meetings/meetingTypeChoice.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { createManualMeeting } from '@/lib/meetings.ts';
import { fetchMeetingTemplates } from '@wundo/client/memberMeetingTemplates.ts';
import { m } from '@/paraglide/messages.js';
import type { MeetingTemplateItem } from '@wundo/shared/meetingTemplate.ts';

/**
 * The last step for somebody without a calendar: one conversation, written down
 * by hand. It will not move on without one and ends on that meeting's own page,
 * so the first thing they see of Wundo is their own meeting being prepared.
 */
export const Route = createFileRoute('/onboarding/first-meeting')({
  component: OnboardingFirstMeetingPage,
});

/** Already carrying a moment: only the title is ever missing at the start. */
function blank(): FirstMeetingDraft {
  const start = defaultStart();

  return {
    title: '',
    attendees: [],
    day: dayValue(start),
    hour: hourValue(start),
    templateId: '',
  };
}

/** A conversation that is ready to be written down: it names one, and a type. */
interface ReadyMeeting extends PlannedMeeting {
  templateId: string;
  attendees: string[];
}

/**
 * The conversation as the preview and the API want it, or null while it is not
 * ready. A missing gesprekstype holds it up only when there is more than one:
 * with exactly one, `defaultMeetingTemplateId` answers the unasked question.
 */
function readyMeeting(
  draft: FirstMeetingDraft,
  templates: MeetingTemplateItem[],
): ReadyMeeting | null {
  const startsAt = momentOf(draft.day, draft.hour);
  const templateId = draft.templateId || defaultMeetingTemplateId(templates);
  if (draft.title.trim() === '' || startsAt === null || templateId === '') {
    return null;
  }

  return {
    id: 'first',
    title: draft.title.trim(),
    startsAt,
    templateId,
    attendees: draft.attendees,
  };
}

export function OnboardingFirstMeetingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FirstMeetingDraft>(blank);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['meetingTemplates', user?.id, 'use'],
    queryFn: () => fetchMeetingTemplates(user!.id, 'use'),
    enabled: Boolean(user?.id),
  });

  const ready = readyMeeting(draft, templates);

  /*
   * The way out is the conversation itself, where its preparation is written.
   * A failure keeps the form: what the user typed is still on screen and the
   * only thing missing is a second attempt.
   */
  const create = useMutation({
    mutationFn: (meeting: ReadyMeeting) =>
      createManualMeeting(user!.id, {
        title: meeting.title,
        starts_at: meeting.startsAt,
        template_id: meeting.templateId,
        attendees: meeting.attendees.map((name) => ({ name })),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      void navigate({
        to: '/meetings/$meetingId',
        params: { meetingId: created.id },
      });
    },
  });

  const change = (patch: Partial<FirstMeetingDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  return (
    <Card
      padding='none'
      className='mx-auto flex w-full max-w-225 overflow-hidden max-agenda:flex-col'
    >
      <div className='min-w-0 flex-1 px-9 py-10'>
        <p className='mb-3.5 text-xs font-bold tracking-wider text-ink-muted uppercase'>
          {m.onboarding_first_meeting_kicker()}
        </p>
        <h1 className='mb-2 text-xl font-semibold tracking-tight'>
          {m.onboarding_first_meeting_title()}
        </h1>
        <p className='mb-6 text-md text-ink-muted'>
          {m.onboarding_first_meeting_subtitle()}
        </p>

        {create.isError && (
          <p
            role='alert'
            className='mb-5 rounded-lg border border-danger-line bg-danger-fill px-4 py-3 text-sm font-medium text-danger'
          >
            {m.onboarding_first_meeting_failed()}
          </p>
        )}

        <FirstMeetingFields draft={draft} templates={templates} onChange={change} />

        <div className='mt-7 flex items-center gap-3'>
          <Button
            variant='accent'
            shape='block'
            size='lg'
            disabled={user === undefined || create.isPending || ready === null}
            onClick={() => ready !== null && create.mutate(ready)}
          >
            {m.onboarding_first_meeting_continue()}
            <ArrowRight aria-hidden='true' className='size-4' />
          </Button>
          <Button
            variant='ghost'
            shape='block'
            size='lg'
            /*
             * Back to the step before this one on whichever branch this is. An
             * organisation without the calendar integration never saw the ask,
             * and sending them there would only bounce them straight back.
             */
            onClick={() =>
              void navigate({
                to:
                  user?.calendar_enabled === false
                    ? '/onboarding/what-you-get'
                    : '/onboarding/calendar',
              })
            }
          >
            {m.onboarding_back()}
          </Button>
        </div>
      </div>

      <div className='flex min-w-0 flex-1 items-center justify-center border-l border-fill bg-fill-faint p-7.5 max-agenda:border-t max-agenda:border-l-0'>
        <PlannedMeetingsPreview meetings={ready === null ? [] : [ready]} />
      </div>
    </Card>
  );
}
